import React, { useId, useState } from 'react';
import { Flag, Building2, Lock, ChevronDown } from 'lucide-react';
import { getEnv } from '../../config/env';

/**
 * USWDS-style "official government website" banner
 * (https://designsystem.digital.gov/components/banner/).
 *
 * Only intended for deployments hosted on an actual .gov/.mil domain — enable
 * per-environment via REACT_APP_SHOW_GOV_BANNER (runtime window._env_ or
 * build-time .env). Defaults to hidden so non-government deployments are unaffected.
 */
export function isGovBannerEnabled(): boolean {
  return getEnv('REACT_APP_SHOW_GOV_BANNER', 'false').toLowerCase() === 'true';
}

export function GovBanner() {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  if (!isGovBannerEnabled()) {
    return null;
  }

  return (
    <section
      aria-label="Official website of the United States government"
      className="print:hidden bg-gray-100 dark:bg-secondary-950 border-b border-gray-300 dark:border-secondary-700 text-xs text-gray-700 dark:text-secondary-300"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-1 flex flex-wrap items-center gap-1.5">
        <Flag className="h-4 w-4 shrink-0 text-blue-700 dark:text-blue-400" aria-hidden="true" />
        <span>An official website of the United States government</span>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded((prev) => !prev)}
          className="ml-1 inline-flex items-center gap-1 font-semibold text-blue-700 dark:text-blue-400 underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        >
          Here's how you know
          <ChevronDown
            className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>

      {expanded && (
        <div id={contentId} className="border-t border-gray-300 dark:border-secondary-700 bg-white dark:bg-secondary-900">
          <div className="max-w-screen-2xl mx-auto px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex gap-3">
              <Building2 className="h-8 w-8 shrink-0 text-gray-500 dark:text-secondary-400" aria-hidden="true" />
              <p className="text-gray-700 dark:text-secondary-300">
                <strong className="block text-gray-900 dark:text-white">Official websites use .gov</strong>
                A <strong>.gov</strong> website belongs to an official government organization in the United States.
              </p>
            </div>
            <div className="flex gap-3">
              <Lock className="h-8 w-8 shrink-0 text-gray-500 dark:text-secondary-400" aria-hidden="true" />
              <p className="text-gray-700 dark:text-secondary-300">
                <strong className="block text-gray-900 dark:text-white">Secure .gov websites use HTTPS</strong>
                A <strong>lock</strong> (<Lock className="inline h-3 w-3" aria-hidden="true" />) or <strong>https://</strong> means
                you've safely connected to the .gov website. Share sensitive information only on official, secure websites.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
